import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface PaymentStatusBadgeProps {
  /**
   * Dedicated payment_status column from orders table (source of truth).
   * Falls back to interpreting orderStatus for backward compatibility.
   */
  paymentStatus?: 'paid' | 'unpaid' | string;
  /** The raw order status from the database (legacy fallback) */
  orderStatus?: string;
  /** 'pickup' | 'delivery' */
  deliveryType: string;
  /** Compact mode shows only the badge without help text (for table rows) */
  compact?: boolean;
  className?: string;
}

/**
 * Displays payment status separately from the logistics progress bar.
 *
 * Why separate? Payment can happen at different points depending on the flow
 * (Stripe pre-pay, Swish, cash at pickup, invoice). Keeping it decoupled from
 * the logistics timeline means the UI works correctly regardless of which
 * payment method is active — now and in the future.
 */
export function PaymentStatusBadge({ paymentStatus, orderStatus, deliveryType, compact, className }: PaymentStatusBadgeProps) {
  const isPaid = paymentStatus === 'paid' || orderStatus === 'paid';

  if (isPaid) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          Betald
        </Badge>
        {!compact && <span className="text-xs text-muted-foreground">Betalning mottagen</span>}
      </div>
    );
  }

  const helpText =
    deliveryType === 'pickup'
      ? 'Betalas vid upphämtning'
      : 'Betalas enligt överenskommelse';

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
        Betalas vid upphämtning
      </Badge>
      {!compact && deliveryType !== 'pickup' && (
        <span className="text-xs text-muted-foreground">{helpText}</span>
      )}
    </div>
  );
}
